// The client mirror of the server's ONE listing-agent search rule
// (`ListingAgentSearch.cs`, Addendum B B1) — for the one table that searches in
// the browser: a buyer's Associated listings, which loads its whole shortlist.
// Held to the server by the shared fixture tests/fixtures/listing-agent-search-
// cases.json, read by listingAgentSearch.test.ts here and
// ListingAgentSearchParityTests on the server.
//
// Word by word: EVERY word must appear in one of the row's own texts or the
// agent's name, email, phone or firm. A word with 4+ digits also matches the
// phone with formatting stripped. A query of only digits and phone punctuation
// with 4+ digits ALSO matches as one digit run (owner ruling 2026-10-15) — an OR
// beside word by word, so it only adds matches. Case-insensitive, as SQL
// Server's collation is.

import type { ListingAgentBriefDto } from "@/api/buyers";

export const DIGIT_RUN_MINIMUM = 4;

const PHONE_SHAPED = /^[0-9\s().+-]+$/;

export function searchWords(query: string | null | undefined): string[] {
  return (query ?? "").split(/\s+/).filter(Boolean);
}

const digitsOf = (value: string) => value.replace(/[^0-9]/g, "");

/** The stored phone as the server strips it: space, ( ) - . + only — nested
 *  SQL REPLACEs, so a "/" or a non-breaking space stays and the digits around
 *  it do not run together. */
const strippedPhone = (value: string) => value.replace(/[ ()\-.+]/g, "");

/** The digit run a phone-shaped query is ALSO matched as, or null. */
export function phoneDigits(query: string | null | undefined): string | null {
  const trimmed = (query ?? "").trim();
  if (!trimmed || !PHONE_SHAPED.test(trimmed)) return null;
  const digits = digitsOf(trimmed);
  return digits.length >= DIGIT_RUN_MINIMUM ? digits : null;
}

type AgentLike = Pick<ListingAgentBriefDto, "label" | "firmName" | "email" | "phone"> & {
  firstName?: string | null;
  lastName?: string | null;
  isFirmContact?: boolean;
};

function agentWordMatches(agent: AgentLike, word: string): boolean {
  const w = word.toLowerCase();
  // The label stands in for the name the brief DTO does not carry — but only
  // for a named agent: a firm contact's "Firm contact — {Firm}" holds words the
  // server never searches ("firm", "contact"), and its firm is matched below.
  const name = agent.isFirmContact ? null : agent.label;
  const texts = [agent.firstName, agent.lastName, name, agent.email, agent.phone, agent.firmName];
  if (texts.some((t) => (t ?? "").toLowerCase().includes(w))) return true;
  const digits = digitsOf(word);
  return digits.length >= DIGIT_RUN_MINIMUM && strippedPhone(agent.phone ?? "").includes(digits);
}

/**
 * True when a row matches the query: every word in one of `ownTexts` or in the
 * agent, or (phone-shaped query) the digit run in the agent's phone. A blank
 * query matches everything.
 */
export function matchesListingAgentSearch(
  query: string,
  ownTexts: (string | null | undefined)[],
  agent: AgentLike | null | undefined,
): boolean {
  const words = searchWords(query);
  if (words.length === 0) return true;
  const own = ownTexts.map((t) => (t ?? "").toLowerCase());
  const byWords = words.every(
    (word) => own.some((t) => t.includes(word.toLowerCase())) || (agent != null && agentWordMatches(agent, word)),
  );
  if (byWords) return true;
  const run = phoneDigits(query);
  return run != null && agent != null && strippedPhone(agent.phone ?? "").includes(run);
}
